/**
 * Journey: IUI (Intrauterine Insemination)
 *
 * Steps: Learn > Test > Clinic > Prep > Treatment > Wait
 *
 * IUI is less invasive than IVF — medications stimulate 1-3 eggs,
 * then washed sperm is placed directly in the uterus.
 * Often the first-line treatment before IVF.
 */

import type { JourneyDefinition, PlanStep } from "../plan.types";

const learnStep: PlanStep = {
  id: "learn",
  title: "How IUI Works",
  subtitle: "A less invasive fertility treatment",
  icon: "BookOpen",
  education: {
    context:
      "IUI (intrauterine insemination) places washed, concentrated sperm directly into your uterus around ovulation. It's less invasive and less expensive than IVF, and is often the first treatment recommended.",
    details:
      "The process:\n1. Monitoring (days 1-12): Ultrasounds and blood work track follicle growth\n2. Trigger shot (day 12-14): An injection triggers ovulation 36 hours later\n3. Insemination (15-20 minutes): Washed sperm is placed via a thin catheter\n4. Two-week wait: You wait ~14 days for a pregnancy test\n\nWith medications (Clomid or Letrozole), success rates are 10-20% per cycle. Most clinics recommend 3-4 IUI cycles before considering IVF.",
    learnMoreLink: "iui-guide",
  },
  actions: [
    {
      label: "I understand this",
      variant: "primary",
      icon: "Check",
      signal: "complete-step",
    },
    {
      label: "Full IUI guide",
      variant: "outline",
      icon: "BookOpen",
      screen: "LearnScreen",
      params: { article: "iui-guide" },
    },
  ],
  completionSignal: { type: "manual" },
  conditionModifiers: [
    {
      condition: "pcos",
      additionalEducation:
        "You mentioned PMOS (ex PCOS). IUI with Letrozole is often first-line for PMOS (ex PCOS). It's effective and lower risk than gonadotropins. Your clinic will monitor closely to avoid multiple pregnancies.",
    },
    {
      condition: "endometriosis",
      additionalEducation:
        "You mentioned endometriosis. Mild endometriosis (stage I-II) responds well to IUI. For moderate-severe, your clinic may recommend moving directly to IVF.",
    },
    {
      condition: "male-factor",
      additionalEducation:
        "IUI can help with mild male factor infertility by concentrating the healthiest sperm. If post-wash total motile count is below 5 million, your clinic may recommend IVF with ICSI instead.",
    },
  ],
  ageSpecificContent: [
    {
      range: [0, 34],
      content:
        "Under 35, IUI success rates are 15-20% per cycle with medication. Most clinics recommend 3-4 cycles.",
    },
    {
      range: [35, 37],
      content:
        "At 35-37, IUI success rates are ~10-15% per cycle. Your clinic may suggest fewer IUI cycles before moving to IVF.",
    },
    {
      range: [38, 40],
      content:
        "At 38-40, IUI success rates drop to ~5-10%. Some clinics recommend going directly to IVF at this age.",
    },
    {
      range: [41, 99],
      content:
        "Over 40, IUI success rates are low (~5%). Your RE will likely recommend IVF or IVF with donor eggs.",
    },
  ],
};

const testStep: PlanStep = {
  id: "test",
  title: "Baseline Testing",
  subtitle: "Confirm IUI is right for you",
  icon: "TestTube",
  education: {
    context:
      "Before starting IUI, your clinic needs to confirm open fallopian tubes (at least one), adequate ovarian reserve, and acceptable sperm parameters. These results help decide if IUI is a good fit or if IVF would be more effective.",
  },
  actions: [
    {
      label: "Order at-home AMH test",
      variant: "primary",
      screen: "AtHomeFertilityTesting",
    },
    {
      label: "Upload test results",
      variant: "outline",
      screen: "DocumentsScreen",
    },
  ],
  substeps: [
    {
      id: "amh",
      title: "AMH (Anti-Müllerian Hormone)",
      education:
        "Measures your egg supply. Helps your clinic choose the right medication protocol.",
      status: "not-started",
    },
    {
      id: "fsh-estradiol",
      title: "FSH + Estradiol",
      education: "Cycle day 2-3 blood test. Shows ovarian function.",
      status: "not-started",
    },
    {
      id: "hsg",
      title: "HSG (Hysterosalpingogram)",
      education:
        "Confirms at least one fallopian tube is open. This is required for IUI to work. Some clinics use a saline sonogram (SIS) instead.",
      status: "not-started",
    },
    {
      id: "infectious-disease",
      title: "Infectious disease screening",
      education:
        "Required before any fertility treatment. Standard panel: HIV, Hepatitis B/C, syphilis, gonorrhea, chlamydia.",
      status: "not-started",
    },
  ],
  partnerTrack: {
    education:
      "Your partner's sperm parameters determine if IUI is viable. Post-wash total motile count above 5-10 million is ideal.",
    substeps: [
      {
        id: "partner-sa",
        title: "Semen analysis",
        education:
          "Measures count, motility, and morphology. Post-wash count determines IUI viability.",
        status: "not-started",
        isPartnerTrack: true,
      },
      {
        id: "partner-infectious",
        title: "Infectious disease screening",
        education: "Required for both partners. Standard panel.",
        status: "not-started",
        isPartnerTrack: true,
      },
    ],
  },
  completionSignal: { type: "all-substeps" },
};

const clinicStep: PlanStep = {
  id: "clinic",
  title: "Find Your Clinic",
  subtitle: "Most REIs offer IUI",
  icon: "Building2",
  education: {
    context:
      "Most reproductive endocrinology clinics offer IUI. It's a simpler procedure than IVF, so clinic success rate differences are less dramatic, but experience still matters.",
  },
  actions: [
    {
      label: "Browse clinics near you",
      variant: "primary",
      screen: "FindCareScreen",
      params: { filter: "iui" },
    },
    {
      label: "Compare clinic costs",
      variant: "outline",
      screen: "CostsOverviewScreen",
    },
  ],
  substeps: [
    {
      id: "research-clinics",
      title: "Research clinics",
      education: "Look for clinics with IUI experience and good reviews.",
      status: "not-started",
    },
    {
      id: "schedule-consultation",
      title: "Schedule a consultation",
      education:
        "Initial consultation to review your case and confirm IUI is appropriate.",
      status: "not-started",
    },
    {
      id: "choose-clinic",
      title: "Choose your clinic",
      education: "Your clinic will start your onboarding process.",
      status: "not-started",
    },
  ],
  completionSignal: { type: "manual" },
};

const prepStep: PlanStep = {
  id: "prep",
  title: "Get Ready for Your Cycle",
  subtitle: "Pre-cycle setup",
  icon: "ClipboardCheck",
  education: {
    context:
      "IUI prep is simpler than IVF. Your clinic will confirm your protocol (natural vs. medicated), start any medications, and schedule monitoring.",
  },
  actions: [
    {
      label: "Continue medical history",
      variant: "primary",
      screen: "MedicalHistory",
    },
    {
      label: "Upload insurance card",
      variant: "secondary",
      screen: "CoverageFlow",
    },
  ],
  substeps: [
    {
      id: "medical-history",
      title: "Complete medical history",
      education: "Identifies factors affecting your protocol.",
      status: "not-started",
    },
    {
      id: "insurance-card",
      title: "Upload insurance card",
      education:
        "Needed for billing. IUI is more likely to be covered than IVF.",
      status: "not-started",
    },
    {
      id: "medication-protocol",
      title: "Confirm medication protocol",
      education:
        "Your clinic will prescribe Clomid (oral) or Letrozole (oral), or in some cases injectable gonadotropins for stronger stimulation.",
      status: "not-started",
    },
    {
      id: "consent-forms",
      title: "Sign consent forms",
      education: "Covers procedure, risks, and multiple pregnancy risk.",
      status: "not-started",
    },
    {
      id: "supplements",
      title: "Start supplements (optional)",
      education:
        "CoQ10 and vitamin D for egg quality. Start 2-3 months before if possible.",
      status: "not-started",
      actions: [
        {
          label: "See supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      ],
    },
  ],
  partnerTrack: {
    education: "Your partner's health info is needed for consent forms.",
    substeps: [
      {
        id: "partner-medical-history",
        title: "Partner: complete medical history",
        education: "Needed for clinic consent forms.",
        status: "not-started",
        isPartnerTrack: true,
      },
    ],
  },
  completionSignal: {
    type: "completion-percent",
    field: "prepReadiness",
    threshold: 80,
  },
};

const treatmentStep: PlanStep = {
  id: "treatment",
  title: "Your IUI Cycle",
  subtitle: "Monitoring, trigger, and insemination",
  icon: "Syringe",
  education: {
    context:
      "Your IUI cycle is active. Track medications, appointments, and symptoms in the Care section.",
    details:
      "A typical IUI cycle unfolds over 2-3 weeks:\n\n• Day 3: Start cycle medications\n• Days 8-12: Monitoring visits (ultrasound + bloodwork)\n• Trigger: Shot when follicles are ready, precisely timed\n• Insemination: 36 hours after trigger; a thin catheter places prepared sperm in your uterus\n• Two-week wait: Before your pregnancy blood test\n\nYour Care page has your medication schedule, symptom tracker, and appointment calendar.",
  },
  actions: [
    {
      label: "Go to Care",
      variant: "primary",
      icon: "Heart",
      screen: "CareOverview",
    },
    { label: "Set your start date", variant: "outline", icon: "Calendar" },
  ],
  completionSignal: { type: "external" },
};

const waitStep: PlanStep = {
  id: "wait",
  title: "The Two-Week Wait",
  subtitle: "Between insemination and pregnancy test",
  icon: "Clock",
  education: {
    context: "After IUI, you wait about 14 days for a pregnancy blood test.",
    details:
      "What's happening:\nDays 1-3: Sperm meets egg in the fallopian tube, fertilization occurs\nDays 4-6: The fertilized egg travels to the uterus and begins implanting\nDays 7-10: Implantation completes, hCG starts being produced\nDays 11-14: hCG rises to detectable levels\n\nWhat you can do:\n• Continue progesterone if prescribed\n• Normal activity is fine\n• Avoid: heavy alcohol, new intense exercise\n• Home tests before day 12 are unreliable, so wait for the blood test",
  },
  actions: [
    {
      label: "Log how you're feeling today",
      variant: "primary",
      icon: "Heart",
    },
    { label: "When is my blood test?", variant: "outline", icon: "Calendar" },
  ],
  completionSignal: { type: "external" },
};

export const IUI_PARTNER: JourneyDefinition = {
  key: "iui-with-partner",
  gender: "female",
  goal: "pregnancy",
  pathway: "with-partner",
  title: "IUI",
  description: "Intrauterine insemination",
  steps: [learnStep, testStep, clinicStep, prepStep, treatmentStep, waitStep],
};
